import {
	ChangeEvent,
	useMemo, useState,
} from 'react';
import {
	debounce, uniq,
} from 'lodash';
import { useQuery } from '@apollo/client';
import { Loader } from 'rsuite';
import {
	AvatarSize,
	BackArrowIcon,
	BanIcon,
	Button,
	ButtonColor,
	Checkbox,
	DataSourceIcon,
	Dialog,
	Input, Label, Menu,
	Zoom,
} from '@aleph/shared-components';
import useCreateRedditReport from 'wb-reddit/app/routes/reports/create/useCreateRedditReport';
import { useTranslation } from 'react-i18next';
import {
	AuthGraphSpotifyReportDataSourcesDocument,
	AuthGraphSpotifyReportDataSourcesQueryItems,
} from 'wb-spotify/graphql/authgraph/generated';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import {
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';
import dayjs from 'dayjs';

const SpotifyDatasourceSelector = ({
	onClose,
}) => {
	const { t } = useTranslation();
	const { lifetimeStats } = useSupergridSettings();

	const {
		filters,
		setFilters,
		setBreakdowns,
		metrics,
		setMetrics,
		setUserGroupIds,
		setDates,
	} = useCreateRedditReport();

	const [value, setValue] = useState<string>('');
	const [search, setSearch] = useState<string>('');

	const [selectedUserGroupIDs, setSelectedUserGroupIDs] = useState<string[]>(filters?.userGroupId?.[0]?.value ?? []);

	const debouncedSetSearch = useMemo(() => debounce(setSearch, 300), []);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setValue(newValue);
		debouncedSetSearch(newValue);
	};

	const saveSelectedDatasources = () => {
		setDates({ from: dayjs().subtract(1, 'week'), to: dayjs() });

		setFilters(curr => ({
			...curr,
			userGroupId: [{ value: uniq(selectedUserGroupIDs) }],
		}));

		setUserGroupIds(uniq(selectedUserGroupIDs));

		if (Object.keys(filters).length === 0) {
			setBreakdowns(['campaignName']);
		}

		if ((metrics ?? []).length === 0) {
			if (lifetimeStats) {
				setMetrics(SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED[0].metrics.map((item => item.value)));
			} else {
				setMetrics(SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED[0].metrics.map((item => item.value)));
			}
		}

		onClose();
	};

	const { data, loading } = useQuery(AuthGraphSpotifyReportDataSourcesDocument, {
		variables: {
			userGroupName: `*${search}*`,
		},
	});

	const toggleDatasourceGroup = (datasourceGroup: AuthGraphSpotifyReportDataSourcesQueryItems) => () => {
		setSelectedUserGroupIDs(curr => {
			if (curr.includes(datasourceGroup.id)) {
				return curr.filter(id => id !== datasourceGroup.id);
			}

			return [...curr, datasourceGroup.id];
		});
	};

	const userGroups = data?.userGroups?.items ?? [];

	return (
		<Dialog
			onClose={onClose}
			className="max-w-[600px] w-full"
			isOpen
			data-testid="datasource-container"
		>
			<Dialog.Header preset={{
				prefix: <DataSourceIcon />,
				title:  t('re: reports.select_data_source', 'Select data source'),
			}}
			/>
			<Dialog.Content>
				<Input
					onChange={onChange}
					value={value}
					placeholder={t('sc: search acc groups', 'Search accounts and teams...')}
					data-testid="datasource-input"
					size="large"
					prefix={
						<Zoom />
					}
					className="w-full"
					autoFocus
					clearable
				/>

				<div className="flex-1 pt-[16px] overflow-hidden flex flex-col">
					{
						loading
						&& <div className="flex items-center justify-center h-full">
							<Loader
								content={t('re: reports.datasources.loading', 'Loading datasources...')}
							/>
						</div>
					}

					{
						!loading
						&& userGroups.length === 0
						&& <div className="flex items-center justify-center h-full">
							{t('re: reports.datasources.no_data', 'No data sources found for your search')}
						</div>
					}

					{
						!loading
						&& userGroups.length > 0
						&& <>
							<div className="flex items-center px-[8px] text-[11px] text-gray-600 gap-[16px] h-[32px]">
								<div className="flex-1">SOURCES</div>
								<div className="w-[16px]" />
							</div>
							<Menu className="overflow-y-auto flex-1">
								{
									userGroups.map((accessGroup, catIdx) => (
										<Menu.Item
											key={catIdx}
											size="large"
											onClick={toggleDatasourceGroup(accessGroup)}
										>
											<RedditEntityImage
												entity={accessGroup}
												size={AvatarSize.Medium}
											/>
											<Label>{accessGroup.name}</Label>
											<Checkbox
												checked={selectedUserGroupIDs.includes(accessGroup.id)}
												onClick={toggleDatasourceGroup(accessGroup)}
											/>
										</Menu.Item>
									))
								}
							</Menu>
						</>
					}
				</div>
			</Dialog.Content>
			<Dialog.Footer>
				<Button
					title={t('sc: discard changes', 'Discard changes')}
					leftNode={<BanIcon />}
					appearance="ghost"
					color={ButtonColor.RED}
					onClick={onClose}
					data-testid="discard-changes-button"
				/>

				<Button
					title={t('sc: confirm', 'Confirm')}
					leftNode={<BackArrowIcon />}
					disabled={selectedUserGroupIDs.length === 0}
					onClick={saveSelectedDatasources}
					data-testid="confirm-changes-button"
				/>
			</Dialog.Footer>
		</Dialog>
	);
};

export default SpotifyDatasourceSelector;
